import { groupBy } from 'lodash';
import { type TextLayer } from '@meitu/saas-editor-core/es/layers/text-layer';
import { type ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { type FontItem } from '@meitu/xdesign-widgets';
import { FontFamilySelector } from '@/components/font-family-selector';
import { useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { useTextRangeAttrsState } from '@/hooks/use-text-range-attrs-state';
import { getUsingFonts } from '@/utils/font';
import { useLayerProps } from '../../hooks';
import { type WidgetCommonProps } from '../../types';
import { Tracker } from '../../utils';

type Props = WidgetCommonProps & {
    fonts: FontItem[];
    enableRemoteSearch?: boolean;
};

export const FontFamily: React.FC<Props> = ({
    fonts,
    enableRemoteSearch = true,
    layerId,
    recordId,
    isVIP,
    onOpenVIPModal,
    isIntl,
}) => {
    const editor = useEditor();
    const textRangeAttrsState = useTextRangeAttrsState(layerId);

    const props = useLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as ITextLayerProps;
        const { fontFamily, advancedTextMaterial } = layerData;

        return {
            fontFamily,
            advancedTextMaterial,
        };
    });

    const fontFamily = props?.fontFamily;
    const selectionFontFamily =
        textRangeAttrsState?.type === 'uniform' || textRangeAttrsState?.type === 'mixed'
            ? textRangeAttrsState.attrs.fontFamily
            : null;

    const popoverWidgetRef = useGlobalWidget(editor, false);

    const finalFontFamily = (() => {
        if (selectionFontFamily) return selectionFontFamily;
        return fontFamily ? fontFamily : undefined;
    })();

    const usingFonts = getUsingFonts(editor, editor.services.frame.getActiveFrameId());

    const handleOpenChange = (open: boolean) => {
        popoverWidgetRef.current = open;

        if (open) {
            Tracker.openFontFamilySelector({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
            });
        }
    };

    const handleChange = (data: FontItem, fontName: string, isFromSearch: boolean) => {
        editor.getInstance<TextLayer>('TextLayer').setFontFamily(layerId, fontName);

        Tracker.setFontFamily({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
            font: data,
            isSearch: isFromSearch,
        });
    };

    return (
        <FontFamilySelector
            value={finalFontFamily}
            fonts={groupBy(fonts, 'category_name')}
            isVip={isVIP}
            onClickVipBtn={onOpenVIPModal}
            usingFonts={usingFonts}
            enableRemoteSearch={!isIntl && enableRemoteSearch}
            onChange={handleChange}
            onDropdownVisibleChange={handleOpenChange}
        />
    );
};
